/** @jsx jsx */
import LogoZapierPlatform from '@zapier/product-dev-platform/src/components/LogoZapierPlatform';
import { Icon, SkipNav } from '@zapier/common-components';
import { Colors, Typography } from '@zapier/theme';
import {
  LOGIN_URL,
  MEDIA_MIN_DESKTOP,
  PLATFORM_ROOT,
  navLinkBaseCss,
} from '@zapier/product-dev-platform/src/constants';
import { HeaderNav, HeaderUser } from '@zapier/product-dev-platform/src/types';
import { css, jsx, Interpolation } from '@emotion/core';

import CtaButtons from './CtaButtons';
import CtaLinks from './CtaLinks';
import Nav from './Nav';
import UserOptions from './UserOptions';

import type { NavItem } from './types';

const defaultFlex = {
  display: 'flex',
  alignItems: 'center',
};
const wrapperCss = {
  backgroundColor: Colors.blackWidow,
  ...Typography.body,
  color: Colors.adamantium,
  padding: '0 30px',

  /** This 1px is taken into account in property top of rightSplitCss. */
  borderBottom: 'solid 1px rgba(255, 255, 255, 0.1)',
};
const containerCss: Interpolation = {
  ...defaultFlex,
  justifyContent: 'space-between',
  position: 'relative',
  height: 55,
  margin: 'auto',
  [MEDIA_MIN_DESKTOP]: {
    height: 68,
  },
};

const rightSplitCss = (isNavOpen: boolean): Interpolation => ({
  position: 'absolute',

  /** This 1px is an offset to property bottom-border of wrapperCss. */
  top: 'calc(100% + 1px)',
  left: -20,
  right: -20,
  zIndex: 1,
  backgroundColor: Colors.white,
  display: isNavOpen ? 'flex' : 'none',
  flexDirection: 'column',
  [MEDIA_MIN_DESKTOP]: {
    ...defaultFlex,
    flexDirection: 'row',
    flex: '1 0 auto',
    alignSelf: 'stretch',
    position: 'relative',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    backgroundColor: 'transparent',
    marginLeft: 40,
  },
});

const onlyMobileCss = {
  [MEDIA_MIN_DESKTOP]: {
    display: 'none',
  },
};
const logoCss: Interpolation = {
  display: 'flex',
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  [MEDIA_MIN_DESKTOP]: {
    position: 'static',
    transform: 'none',
  },
};

type Props = {
  ctaLinks: HeaderNav;
  isNavOpen: boolean;
  isUserOptionsOpen: boolean;
  logoUrl?: string;
  nav: HeaderNav;
  onToggleNav: () => void;
  onToggleUserOptions: () => void;
  /** Location of the website url */
  urlPath?: string;
  user: HeaderUser | undefined | null;
  userLinks: Array<NavItem>;
};

const defaultProps = {
  isFullWidth: false,
  ctaLinks: [],
  userLinks: [],
  logoUrl: PLATFORM_ROOT,
};

const Layout = ({
  ctaLinks,
  isNavOpen,
  isUserOptionsOpen,
  logoUrl,
  nav,
  onToggleNav,
  onToggleUserOptions,
  urlPath,
  user,
  userLinks,
}: Props) => (
  <header css={wrapperCss}>
    <div css={containerCss}>
      <Icon
        css={onlyMobileCss}
        name={isNavOpen ? 'delete' : 'menu'}
        onClick={onToggleNav}
      />
      <a aria-label="Zapier Platform" css={logoCss} href={logoUrl} tabIndex={0}>
        <LogoZapierPlatform theme="dark" width={192} />
      </a>
      <SkipNav
        ariaLabel="Skip to main content"
        skipToSelector={'main'}
        urlPath={urlPath}
      />
      <div css={rightSplitCss(isNavOpen)}>
        <Nav config={nav} />
        {!user ? <CtaButtons /> : <CtaLinks links={ctaLinks} />}
      </div>
      {!user && (
        <a css={css(onlyMobileCss, navLinkBaseCss)} href={LOGIN_URL}>
          Log In
        </a>
      )}
      {user && (
        <UserOptions
          ctaLinks={ctaLinks}
          isUserOptionsOpen={isUserOptionsOpen}
          onToggleUserOptions={onToggleUserOptions}
          optionsLinks={userLinks}
          user={user}
        />
      )}
    </div>
  </header>
);

Layout.defaultProps = defaultProps;

export default Layout;
